import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { SignalDetailResponse, QuoteResponse, CatalystEvent } from '@catalyst-iq/shared';


import { EventData, RecentEvent } from '../types/signal';

import SignalMetricsPanel from '../components/SignalDetail/SignalMetricsPanel';
import WhyThisSignalPanel from '../components/SignalDetail/WhyThisSignalPanel';
import CatalystSidebar from '../components/SignalDetail/CatalystSidebar';
import { CreateAlertForm } from '../components/alerts/CreateAlertForm';
import AddTickerForm from '../components/watchList/AddTickerForm';

export default function SignalDetailPage() {
  const { ticker } = useParams<{ ticker: string }>();
  const [signal, setSignal] = useState<SignalDetailResponse | null>(null);
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [alertMessage, setAlertMessage] = useState<{text: string, type: 'success' | 'error'} | null>(null);
  const [watchlistMessage, setWatchlistMessage] = useState<{text: string, type: 'success' | 'error'} | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0); 
    if (!ticker) return;    
    const safeTicker = ticker.toUpperCase();
    async function fetchAllData() {
      try {
        setLoading(true);
        setError(null);
        const signalData = await api.getSignal(safeTicker);
        setSignal(signalData);
        try {
          const eventsData = await api.getEvents({ ticker: safeTicker, limit: 5 });
          if (eventsData && eventsData.events) {
            const mapped: RecentEvent[] = eventsData.events.map((ev: any) => ({
              time: new Date(ev.ts || (ev.datetime ? ev.datetime * 1000 : Date.now()))
              .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              title: ev.title || ev.headline || 'No title',
              source: ev.source || 'finnhub',
              link: ev.link || ev.url || undefined
            }));
            setRecentEvents(mapped);
          }
        } catch (eventErr: any) {
          console.warn("Downstream events architecture service is offline or timed out", eventErr);
        }
      } catch (err: any) {
        console.error("Failed to load full signal context:", err);
        if (err?.response?.status === 404 || err?.message?.includes('404') || err?.error === 'not_in_scanner') {
          setError('not_in_scanner');
        } else {
          setError(err?.message || 'Unknown error');
        }
      } finally {
        setLoading(false);
      }
    }
    fetchAllData();
  }, [ticker]);

  if (error === 'not_in_scanner' || (!loading && !signal)) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-cat-border bg-white dark:bg-cat-surface shadow-sm overflow-hidden">
          <div className="h-[3px] bg-gradient-to-r from-cat-red via-[#a78bfa] to-cat-accent" />
          <div className="p-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-cat-text-main mb-2">Signal not found</h2>
            <p className="text-[13px] text-gray-500 dark:text-cat-text-muted mb-6">
              The ticker <span className="font-semibold text-gray-800 dark:text-cat-text-main">"{ticker?.toUpperCase()}"</span> is not in the active scanner.
            </p>
            <Link
              to="/scanner"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cat-accent to-cat-accent-2 text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
            >
              Return to Scanner
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-cat-border bg-white dark:bg-cat-surface shadow-sm overflow-hidden">
          <div className="h-[3px] bg-gradient-to-r from-cat-red to-[#a78bfa]" />
          <div className="p-8">
            <h2 className="text-xl font-bold text-cat-red mb-2">Failed to load signal</h2>
            <p className="text-[13px] text-gray-500 dark:text-cat-text-muted">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !signal) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3">
        <div className="w-6 h-6 border-2 border-gray-200 dark:border-cat-border-2 border-t-cat-accent rounded-full animate-spin" />
        <div className="text-[13px] text-gray-400 dark:text-cat-text-muted">
          Loading signal…
        </div>
      </div>
    );
  }

  const enrichedSignalData = {
    ...signal,
    company_name: (signal as any).company_name || (signal as any).companyName || signal.ticker,
    day_range: (signal as any).day_range || (signal as any).dayRange || `${signal.price}-${signal.price}`,
    market_cap: (signal as any).market_cap || (signal as any).marketCap || 'N/A',
    prev_close: (signal as any).prev_close || (signal as any).prevClose || signal.price
  };

  const calculateTimeRel = (detectedAt: string, eventTs: string) => {
    const detectedDate = new Date(detectedAt).getTime();
    const eventDate = new Date(eventTs).getTime();
    const diffMs = Math.abs(detectedDate - eventDate);
    const diffMins = Math.round(diffMs / (1000 * 60));
    return `${diffMins}m ago`;
  };

  const sidebarEvent: EventData | null = signal.event ? {
    id: signal.event.id,
    ticker: signal.ticker,
    title: signal.event.title,
    summary: (signal.event as any).summary || 'No overview breakdown available for this release.', 
    source: signal.event.source,
    link: signal.event.link || '#',
    time_rel: signal.event.ts ? calculateTimeRel(signal.detected_at, signal.event.ts) : 'Just now'
  } : null;
  return (
    <div className="space-y-6 text-gray-900 dark:text-cat-text-main font-sans">
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <div className="flex flex-col gap-6 min-w-0">
          <SignalMetricsPanel signalData={enrichedSignalData as any} />
          
          <WhyThisSignalPanel 
            ruleChain={signal.label_reason} 
            labelReason={signal.label_reason} 
            changePct={signal.change_pct}
            relVolume={signal.rel_volume}
            hasMatchedEvent={!!sidebarEvent}
          />
        </div>

        <div className="flex flex-col gap-4 min-w-0">
          <CatalystSidebar 
            label={signal.label} 
            ticker={signal.ticker}
            recentEvents={recentEvents} 
            event={sidebarEvent}
          />
          
          <div className="flex flex-col gap-4 w-full">
            <div className="w-full">
              <CreateAlertForm 
                initialTicker={signal.ticker}
                fixedTicker={true}
                onSuccess={async () => {
                  setAlertMessage({ text: 'Alert created successfully!', type: 'success' });
                  setTimeout(() => setAlertMessage(null), 3000);
                }} 
              />
              {alertMessage && (
                <div className={`mt-2 p-2.5 rounded-xl text-[12px] font-medium ${alertMessage.type === 'success' ? 'bg-[#10d98e]/10 text-cat-green border border-[#10d98e]/20' : 'bg-[#f5476a]/10 text-cat-red border border-[#f5476a]/20'}`}>
                  {alertMessage.text}
                </div>
              )}
            </div>
            
            <div className="w-full">
              <AddTickerForm 
                initialTicker={signal.ticker}
                fixedTicker={true}
                onAdd={async (tickerName, note) => {
                  try {
                    setWatchlistMessage(null);
                    await api.addToWatchlist({ ticker: tickerName, note });
                    setWatchlistMessage({ text: 'Added to watchlist successfully!', type: 'success' });
                    setTimeout(() => setWatchlistMessage(null), 3000);
                  } catch (err: any) {
                    console.error('Failed to add to watchlist', err);
                    if (err.response?.status === 409) {
                      setWatchlistMessage({ text: `Ticker ${tickerName} is already in your watchlist.`, type: 'error' });
                    } else {
                      setWatchlistMessage({ text: 'Failed to add to watchlist. Please try again.', type: 'error' });
                    }
                    setTimeout(() => setWatchlistMessage(null), 4000);
                  }
                }} 
              />
              {watchlistMessage && (
                <div className={`mt-2 p-2.5 rounded-xl text-[12px] font-medium ${watchlistMessage.type === 'success' ? 'bg-[#10d98e]/10 text-cat-green border border-[#10d98e]/20' : 'bg-[#f5476a]/10 text-cat-red border border-[#f5476a]/20'}`}>
                  {watchlistMessage.text}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
